import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControlLabel, Grid, LinearProgress, Paper, Typography } from '@mui/material';

import { enqueueSnackbarError, enqueueSnackbarSuccess } from 'lib/helpers';
import { updateNfcCardState } from 'lib/models/nfcCards';

import useNfcCardLabels from 'hooks/useNfcCardLabels';

import ConfirmationDialog from 'components/ConfirmationDialog';
import NfcCardExpirationDialog from 'components/NfcCardExpirationDialog';
import NfcCardSummary from 'components/NfcCardSummary';
import WrappingPagination from 'components/ResponsiveTable/WrappingPagination';

import Actions from './Actions';

type Props = {
	data: NfcCard[];
	total: number;
	refetch: () => void;
	loading: boolean;
	page: number;
	rowsPerPage: number;
	handleChangePage: (_: unknown, newPage: number) => void;
	handleChangeRowsPerPage: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
	/** The UIDs of the selected cards, which may include cards of other pages. */
	selected: string[];
	onToggle: (card: NfcCard) => void;
	onTogglePage: (cards: NfcCard[], select: boolean) => void;
	/** What to say when the page comes back empty; nothing when the listing failed. */
	emptyMessage: string | null;
};

export const nfcCardsRowsPerPageOptions = [10, 25, 50, 100];

/**
 * The cards as stacked cards rather than a table, because the section is used from a phone:
 * one column there, more as the screen widens. Each card shows its responsable, balance and
 * number of operations as the listing answers them, has its own actions as big buttons, and
 * a checkbox to pick it for a load of products.
 */
export default function NfcCardsList({
	data,
	total,
	refetch,
	loading,
	page,
	rowsPerPage,
	handleChangePage,
	handleChangeRowsPerPage,
	selected,
	onToggle,
	onTogglePage,
	emptyMessage,
}: Props) {
	const { t } = useTranslation();
	const { cardName } = useNfcCardLabels();

	const [showBlockConfirmation, setShowBlockConfirmation] = useState<NfcCard | null>(null);
	const [updating, setUpdating] = useState<boolean>(false);
	const [expirationCard, setExpirationCard] = useState<NfcCard | null>(null);

	const selectedInPage = data.filter((card) => selected.includes(card._id));
	const allInPageSelected = data.length > 0 && selectedInPage.length === data.length;

	const updateState = async (card: NfcCard, state: 'active' | 'blocked') => {
		setUpdating(true);
		try {
			await updateNfcCardState(card._id, state);
			enqueueSnackbarSuccess(state === 'blocked' ? t('nfc:blocked') : t('nfc:unblocked'));
			refetch();
		} catch (error) {
			enqueueSnackbarError(error);
		}
		setUpdating(false);
	};

	const onBlockHandler = async (mustBlock: boolean) => {
		const card = showBlockConfirmation;
		setShowBlockConfirmation(null);

		if (mustBlock && card) {
			await updateState(card, 'blocked');
		}
	};

	const onExpirationClose = (changed: boolean) => {
		setExpirationCard(null);
		if (changed) refetch();
	};

	return (
		<Box>
			{!!expirationCard && <NfcCardExpirationDialog card={expirationCard} onClose={onExpirationClose} />}

			{showBlockConfirmation && (
				<ConfirmationDialog
					title={t('nfc:blockTitle')}
					description={t('nfc:blockDescription', { card: cardName(showBlockConfirmation.label) })}
					confirmButton={t('nfc:block')}
					onClose={onBlockHandler}
				/>
			)}

			{(!!loading || updating) && <LinearProgress />}

			{!loading && data.length === 0 && !!emptyMessage && (
				<Typography variant="body2" sx={{ paddingY: 2 }}>
					{emptyMessage}
				</Typography>
			)}

			{data.length > 0 && (
				<FormControlLabel
					label={t('nfc:selectAll')}
					control={
						<Checkbox
							indeterminate={selectedInPage.length > 0 && !allInPageSelected}
							checked={allInPageSelected}
							onChange={() => onTogglePage(data, !allInPageSelected)}
						/>
					}
					sx={{ marginBottom: 1 }}
				/>
			)}

			<Grid container spacing={2}>
				{data.map((card) => {
					const isSelected = selected.includes(card._id);

					return (
						<Grid item key={card._id} xs={12} sm={6} lg={4}>
							<Paper
								variant="outlined"
								sx={{
									padding: 2,
									height: '100%',
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'space-between',
									gap: 2,
									borderColor: isSelected ? 'primary.main' : undefined,
								}}
							>
								<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 1 }}>
									<Checkbox
										checked={isSelected}
										onChange={() => onToggle(card)}
										inputProps={{ 'aria-label': t('nfc:selectOne', { card: cardName(card.label) }) }}
										sx={{ marginTop: -1, marginLeft: -1 }}
									/>
									<NfcCardSummary card={card} />
								</Box>

								<Actions
									card={card}
									disabled={updating}
									onBlock={setShowBlockConfirmation}
									onUnblock={(card) => updateState(card, 'active')}
									onChangeExpiration={setExpirationCard}
								/>
							</Paper>
						</Grid>
					);
				})}
			</Grid>

			{/* The API pages this listing */}
			<WrappingPagination
				count={total}
				page={page}
				rowsPerPage={rowsPerPage}
				rowsPerPageOptions={nfcCardsRowsPerPageOptions}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
		</Box>
	);
}
